import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Container,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Chip,
    Fade,
    Slide,
    Avatar,
    LinearProgress,
    Skeleton,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    InputAdornment
} from '@mui/material';
import { Send, Payment, HistoryEdu } from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import ColorData from 'Components/CstmTheme/ColorData';
import AdminApi from 'Service/Admin.api';
import { useDispatch } from 'react-redux';
import { startLoading, stopLoading } from '../../Redux/Screen/Action';
import dayjs from "dayjs";

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
    minHeight: '100vh',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(3),
    marginBottom: theme.spacing(4),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
        backgroundColor: `${theme.palette.action.hover}50`,
    },
    '&:hover': {
        backgroundColor: `${theme.palette.primary.main}08`,
    },
}));

interface EMitraCase {
    id: number;
    description: string;
    caseTypeName: string;
    suggestedAmount: number;
    clientName: string;
    statusLastUpdated: string;
    status: string;
    emitraPhonenumber: string;
    emitraEmail: string;
}

const EMitraUserDetails = () => {
    console.log("EMitraUserDetails component rendered");
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [user, setUser] = useState<any>(location.state?.user || null);
    const [cases, setCases] = useState<EMitraCase[]>([]);
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState<{ [key: string]: string }>({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    useEffect(() => {
        console.log("EMitraUserDetails mounted with ID:", id, "and user from state:", user);
        if (!user && id) {
            // We'll rely on the case fetch to populate the basic user info if missing
        }
        if (id) {
            fetchUserCases(id);
        }
    }, [id, user]);

    const fetchUserCases = async (eMitraId: string) => {
        setLoading(true);
        dispatch(startLoading({ loading: true, loadtext: "Fetching Cases..." }));
        try {
            console.log("Fetching cases for eMitra ID:", eMitraId);
            const response = await AdminApi.getUnpaidCasesForEmitra(eMitraId);
            console.log(response, 'response');
            if (response.success && response.data) {
                console.log("Fetched cases:", response.data);
                setCases(response.data);

                // If user wasn't passed via location state, try to construct basic details from the first case
                if (!user && response.data.length > 0) {
                    const firstCase = response.data[0];
                    if (firstCase.emitraName) {
                        const nameParts = firstCase.emitraName.split(' ');
                        setUser({
                            firstName: nameParts[0] || 'Unknown',
                            lastName: nameParts.slice(1).join(' ') || '',
                            email: firstCase.emitraEmail || 'N/A',
                            contactNumber: firstCase.emitraPhonenumber || 'N/A'
                        });
                    }
                }
            }
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: 'Failed to fetch cases.', severity: 'error' });
        } finally {
            setLoading(false);
            dispatch(stopLoading());
        }
    };

    const handleAmountChange = (caseId: number, value: string) => {
        setPayments((prev) => ({ ...prev, [caseId]: value }));
    };

    const handleSubmitAll = async () => {
        const enteredCaseIds = Object.keys(payments).filter(caseId => payments[caseId] !== "");

        if (enteredCaseIds.length === 0) {
            setSnackbar({ open: true, message: 'Please enter at least one amount.', severity: 'error' });
            return;
        }

        const hasInvalidAmount = enteredCaseIds.some(caseId => {
            const amount = parseFloat(payments[caseId]);
            return isNaN(amount) || amount <= 0;
        });

        if (hasInvalidAmount) {
            setSnackbar({ open: true, message: 'Please enter a valid positive amount for every case.', severity: 'error' });
            return;
        }

        const entries = enteredCaseIds.map(caseId => ({
            caseId: parseInt(caseId),
            amount: parseFloat(payments[caseId])
        }));

        dispatch(startLoading({ loading: true, loadtext: "Submitting batch..." }));
        try {
            const payload = {
                eMitraId: parseInt(id!),
                entries: entries
            };

            await AdminApi.submitEmitraPayoutRequest(payload);
            setSnackbar({ open: true, message: 'Batch payout request submitted successfully.', severity: 'success' });
            setPayments({}); // Clear form
            fetchUserCases(id!); // Refresh list
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: 'Failed to submit batch payout request.', severity: 'error' });
        } finally {
            dispatch(stopLoading());
        }
    };

    // If we don't have a user yet but are still loading, we can show a loader or just wait.
    // However, if there are NO cases and NO user, it might look empty. 
    // We'll provide a fallback user object to prevent the entire page rendering blank.
    console.log(user, 'user');
    const displayUser = user || {
        firstName: 'e-Mitra',
        lastName: 'User',
        email: 'Loading...',
        contactNumber: 'Loading...'
    };

    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box sx={{ pb: 10 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        sx={{ fontWeight: 'bold', mb: 2 }}
                    >
                        Back to List
                    </Button>

                    <HeaderCard elevation={0} sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexDirection: { xs: 'column', md: 'row' },
                        border: "1px solid #e0e0e0",
                        background: '#ffffff',
                        color: 'text.primary'
                    }}>
                        <Box display="flex" alignItems="center" gap={3}>
                            <Avatar sx={{ width: 64, height: 64, bgcolor: theme.palette.secondary.main, fontSize: 24 }}>
                                {displayUser.firstName?.charAt(0)}{displayUser.lastName?.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {displayUser.firstName} {displayUser.lastName}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Email: {displayUser.email} | Phone: {displayUser.contactNumber}
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Send />}
                            onClick={handleSubmitAll}
                            disabled={Object.keys(payments).length === 0}
                            sx={{ mt: { xs: 2, md: 0 }, borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Submit for Request
                        </Button>
                    </HeaderCard>

                    {/* STATS SUMMARY */}
                    <Box display="flex" gap={2} mb={3}>
                        <Card sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                            <Typography color="text.primary" variant="caption" fontWeight="bold">PENDING CASES</Typography>
                            <Typography variant="h4">{cases?.length}</Typography>
                        </Card>
                        <Card sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                            <Typography color="text.primary" variant="caption" fontWeight="bold">TOTAL PAID VALUE</Typography>
                            <Typography variant="h4" color="primary.main">
                                ₹{Object.values(payments).reduce((sum, val) => sum + (Number(val) || 0), 0).toLocaleString()}
                            </Typography>
                        </Card>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                        Eligible Cases for Payout
                    </Typography>

                    {loading ? (
                        <LinearProgress sx={{ mt: 2 }} />
                    ) : (
                        <Slide direction="up" in timeout={600}>
                            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <TableContainer sx={{ maxHeight: 600 }}>
                                    <Table stickyHeader>
                                        <TableHead sx={{ backgroundColor: "#2a3162" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Case Details</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Completion Date</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: '250px', color: '#fff' }}>Enter Payment Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {cases?.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                                        <Typography variant="h6" color="text.primary">No eligible cases found.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                cases?.map((c) => (
                                                    <TableRow key={c.id} hover>
                                                        <TableCell>
                                                            <Typography fontWeight="500">{c.caseTypeName || `Case #${c.id}`}</Typography>
                                                            <Typography variant="caption" color="text.primary">ID: {c.id} {c.clientName ? `| Client: ${c.clientName}` : ''}</Typography>
                                                        </TableCell>
                                                        <TableCell>{dayjs(c.statusLastUpdated).format("DD MMM YYYY")}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={c.status || "Completed"}
                                                                color={c.status?.toLowerCase().includes("pending") ? "warning" : "success"}
                                                                size="small"
                                                                icon={<HistoryEdu fontSize="small" />}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder={c.suggestedAmount?.toString() || "0.00"}
                                                                type="number"
                                                                value={payments[c.id] || ""}
                                                                onChange={(e) => handleAmountChange(c.id, e.target.value)}
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment position="start">
                                                                        <Typography color="text.primary">₹</Typography>
                                                                    </InputAdornment>
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Slide>
                    )}
                </Box>
            </Fade >

            {/* Removed Individual Payout Dialog */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
};

export default EMitraUserDetails;
